import { Box, Collapse, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Typography } from '@mui/material'
import { createElement, useState } from 'react'
import { ElementFactory, Serializer, QuestionRadiogroupModel } from 'survey-core'
import { ReactQuestionFactory, SurveyQuestionElementBase } from 'survey-react-ui'
import { SurveyTextField } from './components/SurveyTextField'
import { SurveyFileDisplay } from './components/SurveyFileDisplay'
import { DragAndDropFilePicker } from '../../DragAndDropFilePicker'
import { DeleteFile } from '../../../../services/FileService'
import { Modal } from '../../Modal'

const CUSTOM_TYPE = 'surveyradio'

export interface RadioMatrixItem {
  hasTextInput?: boolean
  infoText?: string
  hasUpload?: boolean
}

export type RadioMatrix = Record<string, RadioMatrixItem>

export class SurveyRadio extends QuestionRadiogroupModel {
  constructor (name: string) {
    super(name)
    this.titleLocation = 'hidden'
  }

  getType (): string {
    return CUSTOM_TYPE
  }

  get radioMatrix (): RadioMatrix {
    return this.getPropertyValue('radioMatrix')
  }

  set radioMatrix (val: RadioMatrix) {
    this.setPropertyValue('radioMatrix', val)
  }

  get extraInfo (): string {
    return this.getPropertyValue('extraInfo')
  }

  set extraInfo (val: string) {
    this.setPropertyValue('extraInfo', val)
  }

  get uploadedFileName (): string {
    return this.getPropertyValue('uploadedFileName')
  }

  set uploadedFileName (val: string | null) {
    this.setPropertyValue('uploadedFileName', val)
  }

  get uploadedFileKey (): string {
    return this.getPropertyValue('uploadedFileKey')
  }

  set uploadedFileKey (val: string | null) {
    this.setPropertyValue('uploadedFileKey', val)
  }
}

export function registerRadioQuestion (): void {
  ElementFactory.Instance.registerElement(
    CUSTOM_TYPE,
    (name) => {
      return new SurveyRadio(name)
    }
  )

  Serializer.addClass(
    CUSTOM_TYPE,
    [{
      name: 'radioMatrix',
      category: 'general'
    }, {
      name: 'extraInfo',
      type: 'string',
      category: 'general'
    }, {
      name: 'uploadedFileName',
      type: 'string',
      category: 'general',
      visible: false
    }, {
      name: 'uploadedFileKey',
      type: 'string',
      category: 'general',
      visible: false
    }],
    function () {
      return new SurveyRadio('')
    },
    'radiogroup'
  )

  ReactQuestionFactory.Instance.registerQuestion(CUSTOM_TYPE, (props) => {
    return createElement(SurveyQuestionRadio, props)
  })
}

const SurveyRadioContent = ({ question }: { question: SurveyRadio }): JSX.Element => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  const value = question.value

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const newValue = e.target.value
    question.value = newValue
  }

  const handleFileSelect = async (file: File | undefined): Promise<void> => {
    if (file !== undefined) {
      question.survey.uploadFiles(question, file.name, [file], async (data: any, errors: any) => {
        if (errors != null && errors.length > 0) {
          console.error('File upload errors:', errors)
        }
      })
    }
  }

  const confirmDeleteFile = async (): Promise<void> => {
    const key = question.uploadedFileKey
    if (key != null && key !== '') {
      try {
        await DeleteFile(key)
      } catch (e) {
        console.error('Failed to delete file from server', e)
      }
    }
    question.uploadedFileName = null
    question.uploadedFileKey = null
    setIsDeleteModalOpen(false)
  }

  const mappedChoices = question.choices.map((choice) => {
    const choiceValue = choice.value as string
    return {
      value: choiceValue,
      text: choice.text as string,
      hasTextArea: question.radioMatrix?.[choiceValue]?.hasTextInput ?? false,
      infoText: question.radioMatrix?.[choiceValue]?.infoText ?? '',
      hasUpload: question.radioMatrix?.[choiceValue]?.hasUpload ?? false
    }
  })

  return (
    <>
      <FormControl>
        <FormLabel>{question.fullTitle}</FormLabel>
        <RadioGroup
          name={question.name}
          value={value ?? ''}
          onChange={handleChange}
        >
          {mappedChoices.map((choice) => {
            const choiceValue = choice.value
            const isSelected = value === choiceValue

            return (
              <Box key={choiceValue} sx={{ display: 'flex', flexDirection: 'column' }}>
                <FormControlLabel
                  value={choiceValue}
                  control={<Radio />}
                  label={choice.text}
                />
                <Collapse in={isSelected && choice.infoText !== ''}>
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'text.secondary',
                      fontStyle: 'italic'
                    }}
                  >
                    {choice.infoText}
                  </Typography>
                </Collapse>
                <Collapse in={isSelected && choice.hasTextArea}>
                  <SurveyTextField
                    value={question.extraInfo ?? ''}
                    onChange={(e) => {
                      question.extraInfo = e.target.value
                    }}
                    placeholder="Add details here"
                    data-testid={`${question.name}_extraInfo`}
                  />
                </Collapse>
                <Collapse in={isSelected && choice.hasUpload}>
                  <Box>
                    <Box sx={{ maxWidth: '475px' }}>
                      <DragAndDropFilePicker
                        selectFile={handleFileSelect}
                        useButton
                      />
                    </Box>
                    <SurveyFileDisplay
                      fileName={question.uploadedFileName}
                      fileKey={question.uploadedFileKey}
                      onDelete={() => { setIsDeleteModalOpen(true) }}
                    />
                  </Box>
                </Collapse>
              </Box>
            )
          })}
        </RadioGroup>
      </FormControl>
      {isDeleteModalOpen && (
        <Modal
          open={true}
          title="Remove Related Materials"
          onClose={() => { setIsDeleteModalOpen(false) }}
          onConfirm={() => { void confirmDeleteFile() }}
          confirmButtonText="Remove Uploaded Files"
          bodyContent={(
            <>
              <Typography variant="body1" fontWeight="bold">Are you sure you want to change your related materials upload selection to “no”?</Typography>
              <Typography variant="body1" sx={{ mt: '4px' }}>If you select “no” now, your uploads will be lost.</Typography>
            </>
          )}
        />
      )}
    </>
  )
}

export class SurveyQuestionRadio extends SurveyQuestionElementBase {
  get question (): SurveyRadio {
    return this.questionBase as SurveyRadio
  }

  renderElement (): JSX.Element {
    return <SurveyRadioContent question={this.question} />
  }
}
